import React, { useState, useEffect, useMemo } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';

function buildUrl(path) {
  return `${API_BASE.replace(/\/$/, '')}${path}`;
}

function AdminGallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formTitle, setFormTitle] = useState('');
  const [formFile, setFormFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editFile, setEditFile] = useState(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');

  const editFileObjectUrl = useMemo(() => {
    if (!editFile) return null;
    return URL.createObjectURL(editFile);
  }, [editFile]);

  useEffect(() => () => {
    if (editFileObjectUrl) URL.revokeObjectURL(editFileObjectUrl);
  }, [editFileObjectUrl]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(buildUrl('/api/gallery'))
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data.success) setItems(data.items || []);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formFile) {
      setError('Please choose an image to upload.');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title', formTitle.trim());
      formData.append('image', formFile);
      const res = await fetch(buildUrl('/api/gallery'), {
        method: 'POST',
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setFormTitle('');
        setFormFile(null);
        setItems((prev) => [data.item, ...prev]);
        setShowAddForm(false);
      } else {
        setError(data.message || `Failed to add image (${res.status}).`);
      }
    } catch (err) {
      setError(err && err.message ? err.message : 'Cannot reach server.');
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setEditTitle(item.title || '');
    setEditFile(null);
    setEditError('');
  };

  const closeEdit = () => {
    setEditingItem(null);
    setEditTitle('');
    setEditFile(null);
    setEditError('');
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingItem) return;
    setEditError('');
    setEditSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title', editTitle.trim());
      if (editFile) formData.append('image', editFile);
      const res = await fetch(buildUrl(`/api/gallery/${editingItem._id}`), {
        method: 'PUT',
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success && data.item) {
        setItems((prev) => prev.map((it) => (it._id === data.item._id ? data.item : it)));
        closeEdit();
      } else {
        setEditError(data.message || `Failed to update (${res.status}).`);
      }
    } catch (err) {
      setEditError(err && err.message ? err.message : 'Cannot reach server.');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteConfirmId(id);
  };

  const handleDeleteConfirm = async () => {
    const id = deleteConfirmId;
    if (!id) return;
    setDeleteConfirmId(null);
    setDeletingId(id);
    try {
      const res = await fetch(buildUrl(`/api/gallery/${id}`), { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setItems((prev) => prev.filter((item) => item._id !== id));
      }
    } finally {
      setDeletingId(null);
    }
  };

  const imageSrc = (path) => {
    if (!path) return '';
    const p = String(path).trim();
    if (typeof window !== 'undefined' && p.startsWith('/uploads/')) {
      return window.location.origin + (p.startsWith('/') ? p : '/' + p);
    }
    if (p.startsWith('http://') || p.startsWith('https://')) return p;
    if (typeof window !== 'undefined') return window.location.origin + (p.startsWith('/') ? p : '/' + p);
    return p;
  };

  return (
    <div className="admin-dashboard">
      <h1 className="admin-dashboard-title">Gallery</h1>

      <div className="admin-dashboard-section">
        <h2 className="admin-section-title">Gallery images</h2>

        {!showAddForm ? (
          <button
            type="button"
            className="admin-video-add-toggle-btn"
            onClick={() => setShowAddForm(true)}
          >
            <i className="far fa-plus" /> Add image
          </button>
        ) : (
          <div className="admin-video-form-wrap">
            <div className="admin-video-form-header">
              <h3 className="admin-video-form-title">Add image</h3>
              <button
                type="button"
                className="admin-video-form-close"
                onClick={() => { setShowAddForm(false); setError(''); }}
                aria-label="Close form"
              >
                <i className="far fa-times" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="admin-video-form">
              {error && (
                <div className="admin-auth-error" role="alert">
                  {error}
                </div>
              )}
              <div className="form-group">
                <label htmlFor="gallery-title">Title (optional)</label>
                <input
                  id="gallery-title"
                  type="text"
                  className="form-control"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder="Image title"
                />
              </div>
              <div className="form-group admin-video-upload-group">
                <label htmlFor="gallery-upload">Upload image</label>
                <div className="admin-video-upload-box">
                  <input
                    id="gallery-upload"
                    key={formFile ? 'has-file' : 'no-file'}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp"
                    className="admin-video-upload-input"
                    onChange={(e) => setFormFile(e.target.files?.[0] || null)}
                  />
                  <span className="admin-video-upload-label">
                    {formFile ? formFile.name : 'Click or drop an image (jpg, png, gif, webp)'}
                  </span>
                </div>
              </div>
              <button type="submit" className="admin-auth-submit" disabled={submitting}>
                {submitting ? 'Uploading…' : 'Add image'}
              </button>
            </form>
          </div>
        )}

        <div className="admin-gallery-list-wrap">
          {loading ? (
            <p className="admin-section-description">Loading gallery…</p>
          ) : items.length === 0 ? (
            <p className="admin-section-description">No images yet. Click &quot;Add image&quot; to add one.</p>
          ) : (
          <div
            style={{
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              overflow: 'hidden',
            }}
          >
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
              }}
            >
              <thead>
                <tr
                  style={{
                    background: '#f3f4f6',
                    textAlign: 'left',
                    borderBottom: '1px solid #e5e7eb',
                  }}
                >
                  <th style={{ padding: '0.75rem', color: '#374151', fontWeight: 600 }}>Image</th>
                  <th style={{ padding: '0.75rem', color: '#374151', fontWeight: 600 }}>Title</th>
                  <th style={{ padding: '0.75rem', textAlign: 'right', color: '#374151', fontWeight: 600 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '0.75rem' }}>
                      <div
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: 8,
                          overflow: 'hidden',
                          background: '#f3f4f6',
                        }}
                      >
                        <img
                          src={imageSrc(item.image)}
                          alt={item.title || 'Gallery'}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                      </div>
                    </td>
                    <td style={{ padding: '0.75rem', color: '#374151', fontSize: '0.95rem' }}>
                      {item.title || <span style={{ opacity: 0.65 }}>Untitled</span>}
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'right', verticalAlign: 'middle' }}>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          flexWrap: 'nowrap',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: '0.5rem',
                          width: '100%',
                        }}
                      >
                        <button
                          type="button"
                          className="admin-video-delete-btn"
                          style={{ margin: 0, flexShrink: 0 }}
                          onClick={() => handleDeleteClick(item._id)}
                          disabled={deletingId === item._id || Boolean(editingItem)}
                          aria-label="Delete image"
                        >
                          {deletingId === item._id ? '…' : 'Delete'}
                        </button>
                        <button
                          type="button"
                          className="admin-video-cancel-btn"
                          style={{ margin: 0, flexShrink: 0 }}
                          onClick={() => openEdit(item)}
                          disabled={Boolean(editingItem) || Boolean(deleteConfirmId)}
                          aria-label="Edit image"
                        >
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      </div>

      {editingItem && (
        <div className="admin-confirm-overlay" onClick={closeEdit} role="dialog" aria-modal="true" aria-labelledby="admin-gallery-edit-title">
          <div className="admin-confirm-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <h3 id="admin-gallery-edit-title" className="admin-confirm-title">Edit image</h3>
            <form onSubmit={handleEditSubmit} className="admin-video-form" style={{ marginTop: '0.75rem' }}>
              {editError && (
                <div className="admin-auth-error" role="alert">
                  {editError}
                </div>
              )}
              <div className="form-group">
                <label htmlFor="gallery-edit-title">Title (optional)</label>
                <input
                  id="gallery-edit-title"
                  type="text"
                  className="form-control"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  placeholder="Image title"
                />
              </div>
              <div className="form-group admin-video-upload-group" style={{ marginBottom: 0 }}>
                <label htmlFor="gallery-edit-upload">Replace image (optional)</label>
                <div className="admin-video-upload-box">
                  <input
                    id="gallery-edit-upload"
                    key={editFile ? 'edit-has-file' : 'edit-no-file'}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,.jpg,.jpeg,.png,.gif,.webp"
                    className="admin-video-upload-input"
                    onChange={(e) => setEditFile(e.target.files?.[0] || null)}
                  />
                  <span className="admin-video-upload-label">
                    {editFile ? editFile.name : 'Keep current image or choose a new file'}
                  </span>
                </div>
              </div>
              <div
                style={{
                  borderTop: '1px solid #e5e7eb',
                  marginTop: '0.75rem',
                  paddingTop: '0.75rem',
                }}
              >
                <p style={{ fontSize: '0.8rem', fontWeight: 600, margin: '0 0 0.5rem', color: '#374151' }}>
                  {editFileObjectUrl ? 'New image preview' : 'Current image'}
                </p>
                <div
                  style={{
                    padding: '0.65rem',
                    background: '#fafafa',
                    borderRadius: 6,
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <img
                    src={editFileObjectUrl || imageSrc(editingItem.image)}
                    alt=""
                    style={{
                      width: '100%',
                      maxHeight: 200,
                      objectFit: 'contain',
                      borderRadius: 6,
                      display: 'block',
                      background: '#fff',
                    }}
                  />
                </div>
              </div>
              <div className="admin-confirm-actions" style={{ marginTop: '1rem', justifyContent: 'flex-end' }}>
                <button type="button" className="admin-confirm-cancel" onClick={closeEdit}>
                  Cancel
                </button>
                <button type="submit" className="admin-auth-submit" style={{ width: 'auto', marginTop: 0 }} disabled={editSubmitting}>
                  {editSubmitting ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirmId && (
        <div className="admin-confirm-overlay" onClick={() => setDeleteConfirmId(null)} role="dialog" aria-modal="true" aria-labelledby="admin-confirm-title">
          <div className="admin-confirm-box" onClick={(e) => e.stopPropagation()}>
            <h3 id="admin-confirm-title" className="admin-confirm-title">Delete image?</h3>
            <p className="admin-confirm-text">This cannot be undone.</p>
            <div className="admin-confirm-actions">
              <button type="button" className="admin-confirm-cancel" onClick={() => setDeleteConfirmId(null)}>Cancel</button>
              <button type="button" className="admin-confirm-delete" onClick={handleDeleteConfirm}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminGallery;
